import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Bell, BookOpen, Building2, CalendarDays, ChevronRight, CircleHelp, CreditCard, FileText, LogOut, Settings, ShieldCheck, User, UserRound } from 'lucide-react';
import './Header.css';
import './Attention.css';
import { useAuth } from '../../context/AuthContext';
import { imageUrl } from '../../utils/media';
import { api } from '../../services/api';
import Brand from '../Brand';
import WebPushControl from './WebPushControl';

export const Header = () => {
  const { user, logout, selectCenter, resetCenterContext } = useAuth();
  const navigate = useNavigate();
  const displayName = (user?.first_name || user?.username || 'Usuario').trim().split(/\s+/)[0];
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'Miembro Pulsora';
  const isPersonalProfile = !user?.global_access && Boolean(user?.member_id) && !user?.active_center;
  const isCenterWorkspace = !user?.global_access && Boolean(user?.active_center);
  const isCenterManager = isCenterWorkspace && ['Propietario', 'Administrador'].includes(user?.role);
  const hasProfileMenu = isPersonalProfile || isCenterManager;
  const centerName = user?.active_center?.nombre || 'Mi Centro';
  const profileImage = isCenterManager ? user?.active_center?.logo : user?.profile_photo;
  const operationalCenters = (user?.centers || []).filter(center => (center.roles || []).some(role => ['Propietario', 'Administrador'].includes(role)));
  const currentCenterId = Number(user?.active_center?.centro_id || 0);
  const [notifications, setNotifications] = useState([]);
  const [memberPaymentAttention, setMemberPaymentAttention] = useState(0);
  const [personalProgrammingCenterId, setPersonalProgrammingCenterId] = useState(null);
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  const loadNotifications = async () => {
    try {
      const data = await api.notifications.getAll();
      setNotifications(Array.isArray(data) ? data : data.results || []);
    } catch (_) { /* una notificación no debe bloquear la cabecera */ }
  };

  const loadMemberAttention = async () => {
    if (!isPersonalProfile) { setMemberPaymentAttention(0); return; }
    try {
      const data = await api.notifications.getAttention();
      setMemberPaymentAttention(Number(data?.payments || 0));
    } catch (_) { /* el menú de perfil sigue disponible aunque falle el contador */ }
  };

  const loadPersonalProgrammingAccess = async () => {
    if (!isPersonalProfile) { setPersonalProgrammingCenterId(null); return; }
    try {
      const data = await api.portal.getSummary();
      const eligibleCenter = (data?.centers || []).find(center =>
        center?.membership?.estado === 'activa' &&
        center?.membership?.programacion_personal
      );
      setPersonalProgrammingCenterId(eligibleCenter?.centro_id || null);
    } catch (_) {
      setPersonalProgrammingCenterId(null);
    }
  };

  useEffect(() => {
    loadNotifications();
    loadMemberAttention();
    loadPersonalProgrammingAccess();
    const refresh = () => { loadNotifications(); loadMemberAttention(); };
    window.addEventListener('pulsora:notifications-updated', refresh);
    window.addEventListener('pulsora:attention-updated', refresh);
    const timer = window.setInterval(refresh, 20000);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pulsora:notifications-updated', refresh);
      window.removeEventListener('pulsora:attention-updated', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPersonalProfile]);

  useEffect(() => {
    const close = event => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setOpen(false);
      if (profileRef.current && !profileRef.current.contains(event.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const unread = useMemo(() => notifications.filter(item => !item.leida).length, [notifications]);

  const toggleNotifications = async () => {
    // PULSORA NOTIFICATION OPEN REFRESH V1
    setProfileOpen(false);

    const next = !open;
    setOpen(next);

    if (next) {
      await loadNotifications();
    }
  };
  const toggleProfile = () => {
    if (!hasProfileMenu) { navigate('/panel'); return; }
    setOpen(false);
    setProfileOpen(current => !current);
  };
  const attentionAreaForUrl = url => {
    if (!url) return null;
    if (url.startsWith('/finances')) return 'payments';
    if (url.startsWith('/members')) return 'members';
    if (url.startsWith('/community')) return 'comments';
    return null;
  };
  const openNotification = async item => {
    setOpen(false);
    try {
      if (item.url && selectCenter) {
        const parsed = new URL(item.url, window.location.origin);
        const centerId = parsed.searchParams.get('center');
        if (centerId && Number(centerId) !== Number(user?.active_center?.centro_id)) await selectCenter(Number(centerId));
      }
      if (!item.leida) {
        await api.notifications.markRead([item.notificacion_id]);
        setNotifications(current => current.map(row => row.notificacion_id === item.notificacion_id ? { ...row, leida: true } : row));
      }
      const area = attentionAreaForUrl(item.url);
      if (area && area !== 'payments') await api.notifications.markAttentionReviewed(area);
      window.dispatchEvent(new Event('pulsora:attention-updated'));
      window.dispatchEvent(new Event('pulsora:notifications-updated'));
    } catch (_) { /* navegar aunque el marcado no responda */ }
    if (item.url) navigate(item.url);
  };
  const goFromProfile = path => { setProfileOpen(false); navigate(path); };
  const enterCenterWorkspace = async centerId => {
    try {
      setProfileOpen(false);
      await selectCenter(Number(centerId));
      navigate('/panel');
    } catch (_) { /* mantener el contexto actual si el Centro no está disponible */ }
  };
  const returnToPersonalProfile = async () => {
    try {
      setProfileOpen(false);
      await resetCenterContext();
      navigate('/panel');
    } catch (_) { /* mantener el workspace actual si no se puede restaurar el perfil */ }
  };
  const closeSession = async () => { setProfileOpen(false); await logout(); };

  return (
    <header className="header">
      <button className="mobile-home-brand" type="button" onClick={() => navigate('/panel')} aria-label="Ir al Panel"><Brand /></button>
      <div className="header-actions">
        <div className="notification-control" ref={notificationRef}>
          <button className="action-btn notification-button" aria-label="Notificaciones" onClick={toggleNotifications}><Bell size={20} />{unread > 0 && <span>{unread > 99 ? '99+' : unread}</span>}</button>
          {open && <div className="notification-popover attention-notification-popover"><header><div><strong>Notificaciones</strong><small>{unread ? `${unread} sin leer` : notifications.length ? 'Todo al día' : 'Sin novedades'}</small></div></header><WebPushControl/><div>{notifications.length ? notifications.slice(0, 16).map(item => <button className={item.leida ? '' : 'notification-unread'} key={item.notificacion_id} onClick={() => openNotification(item)}><b>{item.titulo}</b><span>{item.mensaje}</span><time>{new Date(item.creada_en).toLocaleString('es-PA', { dateStyle: 'short', timeStyle: 'short' })}</time></button>) : <p>No tienes notificaciones nuevas.</p>}</div></div>}
        </div>
        <div className="coach-copy profile-context-copy"><strong>{isCenterManager ? centerName : (isPersonalProfile ? fullName : displayName)}</strong></div>
        <div className="member-profile-control" ref={profileRef}>
          <button className={`action-btn profile-btn${profileImage ? ' has-photo' : ''}${hasProfileMenu ? ' member-profile-trigger' : ''}`} aria-label={hasProfileMenu ? 'Abrir menú de perfil' : 'Ir al Panel'} aria-expanded={hasProfileMenu ? profileOpen : undefined} onClick={toggleProfile}>{profileImage ? <img src={imageUrl(profileImage)} alt={isCenterManager ? `Logo de ${centerName}` : `Foto de ${displayName}`}/> : (isCenterManager ? <Building2 size={20}/> : <User size={20}/>)}{isPersonalProfile && memberPaymentAttention > 0 && <span className="profile-attention-dot" aria-label={`${memberPaymentAttention} avisos de pago`}>{memberPaymentAttention > 9 ? '9+' : memberPaymentAttention}</span>}</button>
          {hasProfileMenu && profileOpen && <div className="member-profile-popover" role="menu">
            <div className="member-profile-summary">
              <div className={`member-profile-avatar${profileImage ? ' has-photo' : ''}`}>{profileImage ? <img src={imageUrl(profileImage)} alt=""/> : (isCenterManager ? <Building2 size={22}/> : <UserRound size={22}/>)}</div>
              <div><strong>{isCenterManager ? centerName : fullName}</strong><span>{isCenterManager ? 'Perfil del Centro' : (user?.email || user?.username)}</span></div>
            </div>
            {isPersonalProfile ? <div className="member-profile-menu member-account-menu">
              <span className="member-menu-section-label">CUENTA</span>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/member-profile')}><UserRound size={18}/><span><b>Mi perfil</b><small>Datos personales, planes y progreso</small></span><ChevronRight size={16}/></button>
              {personalProgrammingCenterId && <button type="button" role="menuitem" onClick={() => goFromProfile(`/my-programming?center=${personalProgrammingCenterId}`)}><CalendarDays size={18}/><span><b>Mi programación</b><small>Tu entrenamiento personalizado</small></span><ChevronRight size={16}/></button>}
              <button type="button" role="menuitem" onClick={() => goFromProfile('/activity')}><Activity size={18}/><span><b>Actividad</b><small>Historial, clases y Centros favoritos</small></span><ChevronRight size={16}/></button>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/my-payments')}><CreditCard size={18}/><span><b>Pagos</b><small>Planes, saldos y comprobantes</small></span>{memberPaymentAttention > 0 && <em>{memberPaymentAttention > 99 ? '99+' : memberPaymentAttention}</em>}<ChevronRight size={16}/></button>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/settings')}><Settings size={18}/><span><b>Ajustes</b><small>Privacidad, notificaciones y seguridad</small></span><ChevronRight size={16}/></button>

              {operationalCenters.length > 0 && <><span className="member-menu-section-label">ADMINISTRAR CENTROS</span>{operationalCenters.map(center => { const visibleRoles = (center.roles || []).filter(role => role !== 'Alumno'); return <button type="button" role="menuitem" className="center-workspace-entry" key={center.centro_id} onClick={() => enterCenterWorkspace(center.centro_id)}><Building2 size={18}/><span><b>{center.nombre}</b><small>{visibleRoles.join(' · ') || 'Administrar Centro'}</small></span><ChevronRight size={16}/></button>; })}</>}

              <span className="member-menu-section-label">SOPORTE</span>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/support')}><CircleHelp size={18}/><span><b>Ayuda</b><small>Consultas y conversaciones</small></span><ChevronRight size={16}/></button>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/information?section=how')}><BookOpen size={18}/><span><b>Cómo funciona Pulsora</b><small>Guía rápida de la aplicación</small></span><ChevronRight size={16}/></button>

              <span className="member-menu-section-label">INFORMACIÓN LEGAL</span>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/information?section=terms')}><FileText size={18}/><span><b>Condiciones de uso</b><small>Reglas generales de la plataforma</small></span><ChevronRight size={16}/></button>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/information?section=privacy')}><ShieldCheck size={18}/><span><b>Política de privacidad</b><small>Uso y protección de tus datos</small></span><ChevronRight size={16}/></button>
            </div> : <div className="member-profile-menu center-profile-menu center-account-menu">
              <span className="member-menu-section-label">CUENTA</span>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/centers')}><Building2 size={18}/><span><b>Perfil del Centro</b><small>Identidad, ubicación, especialidades y fotos</small></span><ChevronRight size={16}/></button>
              <div className="center-profile-payment-group">
                <div className="center-profile-group-title"><CreditCard size={18}/><span><b>Pagos</b><small>Suscripción Pulsora y operación del Centro</small></span></div>
                <button type="button" role="menuitem" onClick={() => goFromProfile('/administration/pulsora-subscription')}><span><b>Pagos Pulsora</b><small>Plan, renovación y facturación SaaS</small></span><ChevronRight size={15}/></button>
                <button type="button" role="menuitem" onClick={() => goFromProfile('/administration/memberships')}><span><b>Membresías</b><small>Planes, vigencias y renovaciones</small></span><ChevronRight size={15}/></button>
                <button type="button" role="menuitem" onClick={() => goFromProfile('/finances')}><span><b>Pagos de miembros</b><small>Cobros, comprobantes y validación</small></span><ChevronRight size={15}/></button>
              </div>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/center-settings')}><Settings size={18}/><span><b>Ajustes</b><small>Privacidad, notificaciones y seguridad</small></span><ChevronRight size={16}/></button>

              {user?.member_id && <><span className="member-menu-section-label">MI PERFIL</span><button type="button" role="menuitem" className="return-personal-profile" onClick={returnToPersonalProfile}><UserRound size={18}/><span><b>Volver a mi perfil</b><small>Clases, Centros, pagos y actividad personal</small></span><ChevronRight size={16}/></button></>}
              {operationalCenters.some(center => Number(center.centro_id) !== currentCenterId) && <><span className="member-menu-section-label">OTROS CENTROS</span>{operationalCenters.filter(center => Number(center.centro_id) !== currentCenterId).map(center => <button type="button" role="menuitem" className="center-workspace-entry" key={center.centro_id} onClick={() => enterCenterWorkspace(center.centro_id)}><Building2 size={18}/><span><b>{center.nombre}</b><small>{(center.roles || []).filter(role => role !== 'Alumno').join(' · ')}</small></span><ChevronRight size={16}/></button>)}</>}

              <span className="member-menu-section-label">SOPORTE</span>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/support')}><CircleHelp size={18}/><span><b>Ayuda</b><small>Consultas y conversaciones</small></span><ChevronRight size={16}/></button>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/center-information?section=how')}><BookOpen size={18}/><span><b>Cómo funciona Pulsora</b><small>Guía rápida para operar el Centro</small></span><ChevronRight size={16}/></button>

              <span className="member-menu-section-label">INFORMACIÓN LEGAL</span>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/center-information?section=terms')}><FileText size={18}/><span><b>Condiciones de uso</b><small>Reglas de uso para Centros</small></span><ChevronRight size={16}/></button>
              <button type="button" role="menuitem" onClick={() => goFromProfile('/center-information?section=privacy')}><ShieldCheck size={18}/><span><b>Política de privacidad</b><small>Uso y protección de datos</small></span><ChevronRight size={16}/></button>
            </div>}
            <button type="button" className="member-profile-logout" onClick={closeSession}><LogOut size={17}/> Cerrar sesión</button>
          </div>}
        </div>
        {!hasProfileMenu && <button className="action-btn" aria-label="Cerrar sesión" title="Cerrar sesión" onClick={logout}><LogOut size={19} /></button>}
      </div>
    </header>
  );
};
